/**
 * The internal resolution (05 §3). The frame is composed at 640x360 "base" units - the playfield
 * 448 wide at 14 texels a tile - and rendered at `scale` times that. By default the scale fills
 * the screen, one texel per screen pixel, so the image is sharp; `pixel` > 1 renders coarser and
 * blows it up with hard edges, for the chunky look (the owner asked for less pixelation, 2026-09-23).
 */

export const BASE_W = 640;
export const BASE_H = 360;
/** Texels per 8-pixel tile at scale 1. */
export const BASE_PPT = 14;

export interface RenderSize {
  /** The visible image, texels. */
  readonly viewW: number;
  readonly viewH: number;
  /** Extra texels rendered on every side, so the sub-texel offset never shows an edge. */
  readonly border: number;
  readonly targetW: number;
  readonly targetH: number;
  /** viewW / 640. */
  readonly scale: number;
}

export function renderSize(scale: number): RenderSize {
  const viewW = Math.round(BASE_W * scale);
  const viewH = Math.round(BASE_H * scale);
  const border = 1;
  return { viewW, viewH, border, targetW: viewW + border * 2, targetH: viewH + border * 2, scale };
}

/**
 * The largest size that fits a `w` x `h` (device pixels) screen with `pixel` screen pixels per
 * texel. The scale is kept to eighths so a tile is always a whole multiple of 1.75 texels.
 */
export function fitRenderSize(w: number, h: number, pixel = 1): RenderSize {
  const s = Math.min(w / BASE_W, h / BASE_H) / Math.max(1, pixel);
  return renderSize(Math.max(1, Math.floor(s * 8) / 8));
}
